import { useEffect, useRef, useState } from "react";
import { Download, FileText, Image, Table2, FileCode, Printer, Presentation, ChevronDown } from "lucide-react";
import { exportCSV, exportHTML, exportPNG, exportXLS, printReport } from "./reportExport";
import type { CacheEntry } from "./reportApi";
import { useTranslation } from "../../i18n";

export function ExportMenu({
  title,
  getNode,
  getEntries,
  onPreview,
}: {
  title: string;
  getNode: () => HTMLElement | null;
  getEntries: () => CacheEntry[];
  /** Switch the builder to Preview mode before a visual export (PDF/Print/PNG/HTML). */
  onPreview?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  async function run(key: string, fn: () => void | Promise<void>) {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      alert(`${t("reportBuilder.exportFailed", "Export failed:")} ${(e as Error).message}. ${t("reportBuilder.exportTryPdf", "Try PDF (Print) instead.")}`);
    } finally {
      setBusy(null);
      setOpen(false);
    }
  }

  const items = [
    { key: "pdf", label: t("reportBuilder.exportPdf", "PDF (print-ready)"), icon: <FileText size={15} />, run: async () => { onPreview?.(); await printReport(getNode()); } },
    { key: "png", label: t("reportBuilder.exportPng", "PNG image"), icon: <Image size={15} />, run: async () => { onPreview?.(); const n = getNode(); if (n) await exportPNG(title, n); } },
    { key: "csv", label: t("reportBuilder.exportCsv", "CSV (data)"), icon: <Table2 size={15} />, run: () => exportCSV(title, getEntries()) },
    { key: "xlsx", label: t("reportBuilder.exportXls", "Excel (.xls)"), icon: <Table2 size={15} />, run: () => exportXLS(title, getEntries()) },
    { key: "html", label: t("reportBuilder.exportHtml", "HTML (shareable)"), icon: <FileCode size={15} />, run: async () => { onPreview?.(); const n = getNode(); if (n) await exportHTML(title, n); } },
    { key: "pptx", label: t("reportBuilder.exportSlides", "Slides-ready (HTML)"), icon: <Presentation size={15} />, run: async () => { onPreview?.(); const n = getNode(); if (n) await exportHTML(title + " (slides)", n); } },
    { key: "print", label: t("reportBuilder.exportPrint", "Print"), icon: <Printer size={15} />, run: async () => { onPreview?.(); await printReport(getNode()); } },
  ];

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
      >
        <Download size={16} /> {t("common.export", "Export")} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 overflow-hidden rounded-lg border border-gray-100 bg-white py-1 shadow-xl">
          {items.map((it) => (
            <button
              key={it.key}
              onClick={() => run(it.key, it.run)}
              disabled={!!busy}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <span className="text-gray-400">{it.icon}</span>
              {busy === it.key ? t("reportBuilder.working", "Working…") : it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
